import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useLocation, useNavigate} from 'react-router';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {useLoading} from '@/app/zustand/useLoading';
import {LINKS} from '@/constants/LinksConstants';
import {useRemoteFeatureFlags} from '@/dataSource/src/api/firebase/useRemoteFeatureFlags';
import {useAmplifyAuth, useCountryCode} from '@/hooks';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {CAwsAmplifyAuthChallenges} from '@/services/amplify/AmplifyConstants';
import {isDevelopment} from '@/utils/environment';
import logError from '@/utils/errors/logError';
import {generateUuid} from '@/utils/randomGenerator';

import {DEV_PASSWORD} from '../../constants/devDefaults';

import {usePasswordVerifyList} from './hooks/usePasswordVerifyList';
import {useShowCompatibilityBanner} from './hooks/useShowCompatibilityBanner';
import {useSignUpCodeValidation} from './hooks/useSignUpCodeValidation';
import {useSignUpErrorToast} from './hooks/useSignUpErrorToast';
import {useSignUpPersonalInfo} from './hooks/useSignUpPersonalInfo';
import {useSignUpQueryParams} from './hooks/useSignUpQueryParams';
import {SIGN_UP_STEP} from './models/constants';
import {SignUpViewModelContextType} from './models/types';
import {useTrackers} from './trackers';

const SignUpViewModelContext = createContext<
  SignUpViewModelContextType | undefined
>(undefined);

export function SignUpViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [PROPERTIES] ----]
  const location = useLocation();
  const signUpData = location.state || {};
  const {isCountryCodeVisible} = useCountryCode();
  const {flagsMap, isLoading: isLoadingFeatureFlags} = useRemoteFeatureFlags();
  const isSignUpDisabled = flagsMap?.isSignUpDisabled ?? false;
  const {isMobile} = useWindowSize();
  const verifyList = usePasswordVerifyList();
  //#endregion

  //#region [---- [STATES] ----]
  const passwordDev = isDevelopment ? DEV_PASSWORD : '';
  const [currentStep, setCurrentStep] = useState<number>(
    SIGN_UP_STEP.PERSONAL_INFO,
  );
  const setStep = (step: number) => setCurrentStep(step);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(false);
  const [isMarketingChecked, setIsMarketingChecked] = useState<boolean>(false);
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    phone,
    setPhone,
    isPhonePrefilledFromInvitation,
    coParentInviterName,
    email,
    setEmail,
  } = useSignUpPersonalInfo(signUpData);
  const [password, setPassword] = useState<string>(passwordDev);
  const [confirmPassword, setConfirmPassword] = useState<string>(passwordDev);
  const {
    role,
    invitationCode,
    setInvitationCode,
    referralCode,
    setReferralCode,
    signUpCode,
    setSignUpCode,
    showInputReferralCode,
    setShowInputReferralCode,
    enableBankTesting,
    queryParamsProcessed,
  } = useSignUpQueryParams(location.search);
  const {isValidSignUpCode, isValidatingCode, invitationCodeWritten} =
    useSignUpCodeValidation({signUpCode, setInvitationCode, setReferralCode});
  const {isLoading: isLoadingAmplifyAuth, signUp: amplifyHandleSignUp} =
    useAmplifyAuth();
  const navigate = useNavigate();
  const [isVerify, setIsVerify] = useState<boolean>(false);

  const {
    trackSelectSignUpPersonalInfoContinue,
    trackApiSignUpPersonalInfoError,
    trackViewSignUpPersonalInfo,
    trackViewSignUpEmailPassword,
    trackSelectSignUpEmailPasswordContinue,
    trackSelectCheckboxOptInMarketingCampaigns,
    trackSelectSignUpGoToSignIn,
  } = useTrackers();
  const {setSkipLoading} = useLoading();
  const getSignUpErrorToast = useSignUpErrorToast();
  const [isEmailError, setEmailError] = useState<boolean>(false);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    if (!queryParamsProcessed) return;
    if (currentStep === SIGN_UP_STEP.PERSONAL_INFO) {
      trackViewSignUpPersonalInfo(
        invitationCode,
        referralCode,
        invitationCodeWritten,
      );
    } else if (currentStep === SIGN_UP_STEP.CONTACT_INFO) {
      trackViewSignUpEmailPassword(
        invitationCode,
        referralCode,
        invitationCodeWritten,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, queryParamsProcessed]);
  //#endregion

  //#region [---- [MEMOS] ----]
  const showCompatibilityBanner = useShowCompatibilityBanner({
    isLoadingFeatureFlags,
    flagsMap,
    role,
    isMobile,
  });

  const handleSignUp = useCallback(async () => {
    if (isSignUpDisabled) return;

    try {
      trackSelectSignUpEmailPasswordContinue(
        invitationCode,
        referralCode,
        invitationCodeWritten,
      );
      const uuid = generateUuid();
      const name = `${firstName} ${lastName}`;
      const shared_key = generateUuid();
      const result = await amplifyHandleSignUp({
        username: uuid,
        password: password,
        email,
        phoneNumber: phone,
        name,
        shared_key,
      });
      if (
        result.nextStep.signUpStep === CAwsAmplifyAuthChallenges.CONFIRM_SIGN_UP
      ) {
        navigate('/authentication/verify', {
          state: {
            phone,
            sharedKey: shared_key,
            isSignUp: true,
            firstName,
            lastName,
            userName: uuid,
            password,
            invitation_code: invitationCode,
            referral_code: referralCode,
            email,
            role,
            marketing_opt_in: isMarketingChecked,
            ...(enableBankTesting !== undefined && {enableBankTesting}),
          },
        });
      }
    } catch (error) {
      trackApiSignUpPersonalInfoError(
        invitationCode,
        referralCode,
        invitationCodeWritten,
        error,
      );
      logError(error, {
        fileName: 'Signup ViewModelContext',
        functionName: 'handleSignUp',
        extraTags: {
          'user.phone': phone,
          'user.email': email,
        },
      });
      const {summary, message} = getSignUpErrorToast(
        error?.name || '',
        error?.message || '',
      );

      showSnackbar({summary, message});
    }
  }, [
    amplifyHandleSignUp,
    email,
    enableBankTesting,
    firstName,
    getSignUpErrorToast,
    invitationCode,
    invitationCodeWritten,
    isMarketingChecked,
    isSignUpDisabled,
    lastName,
    navigate,
    password,
    phone,
    referralCode,
    role,
    trackApiSignUpPersonalInfoError,
    trackSelectSignUpEmailPasswordContinue,
  ]);

  //#endregion

  const isDisabledSignUpStep1 = useMemo(
    () =>
      isSignUpDisabled ||
      !firstName ||
      !lastName ||
      (signUpCode?.length && !isValidSignUpCode) ||
      isValidatingCode,
    [
      firstName,
      isSignUpDisabled,
      isValidSignUpCode,
      isValidatingCode,
      lastName,
      signUpCode?.length,
    ],
  );

  const isDisabledSignUpStep2 = useMemo(
    () =>
      isSignUpDisabled ||
      !phone ||
      !email ||
      !isCheckboxChecked ||
      isEmailError,
    [isSignUpDisabled, phone, email, isCheckboxChecked, isEmailError],
  );

  const isDisabledSignUpStep3 = useMemo(
    () =>
      isSignUpDisabled ||
      !password ||
      !confirmPassword ||
      password !== confirmPassword ||
      !isVerify,
    [confirmPassword, isSignUpDisabled, isVerify, password],
  );
  //#endregion

  //#region [---- [CALLBACKS] ----]
  const handleOnClickSignUpStep1 = useCallback(() => {
    trackSelectSignUpPersonalInfoContinue(
      invitationCode,
      referralCode,
      invitationCodeWritten,
    );
    setSkipLoading(true);
    setStep(SIGN_UP_STEP.CONTACT_INFO);
  }, [
    invitationCode,
    invitationCodeWritten,
    referralCode,
    setSkipLoading,
    trackSelectSignUpPersonalInfoContinue,
  ]);

  const handleOnClickSignUpStep2 = useCallback(() => {
    setSkipLoading(true);
    setStep(SIGN_UP_STEP.PASSWORD);
  }, [setSkipLoading]);

  const handleToggleMarketingChecked = useCallback(
    (checked: boolean) => {
      setIsMarketingChecked(checked);
      trackSelectCheckboxOptInMarketingCampaigns(checked);
    },
    [trackSelectCheckboxOptInMarketingCampaigns],
  );

  const handleOnClickSignIn = useCallback(() => {
    trackSelectSignUpGoToSignIn(
      invitationCode,
      referralCode,
      invitationCodeWritten,
    );
    navigate('/authentication/sign-in');
  }, [
    invitationCode,
    invitationCodeWritten,
    navigate,
    referralCode,
    trackSelectSignUpGoToSignIn,
  ]);

  const handleOpenTermsAndConditions = useCallback(() => {
    window.open(LINKS.TERMS_AND_CONDITIONS, '_blank');
  }, []);

  const handleOpenPrivacyPolicy = useCallback(() => {
    window.open(LINKS.PRIVACY_POLICY, '_blank');
  }, []);
  //#endregion

  //#region [---- [RETURN] ----]
  return (
    <SignUpViewModelContext.Provider
      value={{
        currentStep,
        phone,
        firstName,
        lastName,
        email,
        password,
        confirmPassword,
        invitationCode,
        referralCode,
        signUpCode,
        verifyList,
        showInputReferralCode,
        isDisabledSignUpStep1,
        isDisabledSignUpStep2,
        isDisabledSignUpStep3,
        isValidSignUpCode,
        showCompatibilityBanner,
        isSignUpDisabled,
        isMobile,
        isLoadingAmplifyAuth,
        setStep,
        setPhone,
        setFirstName,
        setLastName,
        setPassword,
        setConfirmPassword,
        setInvitationCode,
        setReferralCode,
        setSignUpCode,
        handleOnClickSignUpStep1,
        handleOnClickSignUpStep2,
        handleSignUp,
        handleOnClickSignIn,
        setEmail,
        setEmailError,
        setIsVerify,
        setShowInputReferralCode,
        setIsCheckboxChecked,
        isCheckboxChecked,
        isMarketingChecked,
        handleToggleMarketingChecked,
        role,
        isCountryCodeVisible,
        isPhonePrefilledFromInvitation,
        coParentInviterName,
        handleOpenTermsAndConditions,
        handleOpenPrivacyPolicy,
      }}>
      {children}
    </SignUpViewModelContext.Provider>
  );
  //#endregion
}

export function useSignUpViewModelProvider() {
  const context = useContext(SignUpViewModelContext);
  if (context === undefined) {
    throw new Error('SignUp ViewModelContext is undefined');
  }
  return context;
}
